import * as Database from './Database';
import { isCopiedThenCorrectedFood } from './Util';
let _ = require('lodash');
const TODOS_PATH = 'todos';
const TODOS_ARCHIVE_PATH = 'todos-archive';

export const GLOBAL_TODO_CACHE = {
  uniqueTodos: []
};

// Calls callback for each new & unhandled todo item. Should take 2 parameters, todoItemId and todoItem object.
// Just need to call this once and it will initialize a listener that stays alive for the lifetime of the app.
export function onNewTodoItem(callback, isArchived) {
  if (isArchived) {
    Database.doOnChildAdded(TODOS_ARCHIVE_PATH, callback);
  } else {
    Database.doOnChildAdded(TODOS_PATH, callback);
  }
}

// Calls callback for each new & unhandled todo item. Should take 2 parameters, todoItemId and todoItem object.
// Just need to call this once and it will initialize a listener that stays alive for the lifetime of the app.
export function onTodoItemUpdate(callback, isArchived) {
  if (isArchived) {
    Database.doOnChildChanged(TODOS_ARCHIVE_PATH, callback);
  } else {
    Database.doOnChildChanged(TODOS_PATH, callback);
  }
}

// Calls callback for each removed todo item. Should take 2 parameters, todoItemId and todoItem object.
// Just need to call this once and it will initialize a listener that stays alive for the lifetime of the app.
// You may or may not need this.
export function onRemovedTodoItem(callback, isArchived) {
  if (isArchived) {
    Database.doOnChildRemoved(TODOS_ARCHIVE_PATH, callback);
  } else {
    Database.doOnChildRemoved(TODOS_PATH, callback);
  }
}

// Returns the todoItemId of the item that was added.
// Call for each item you want to add.
export function addNewTodoItem(key, todoItem) {
  return Database.pushFirebaseEntry(TODOS_PATH, todoItem);
}

// Returns the todoItemId of the item that was added.
// Call for each item you want to add.
export function addNewTodoItemWithCustomPathAndFBID(key, todoItem) {
  return Database.pushFirebaseEntry(TODOS_PATH + "/" + key, todoItem);
}

export function addNewTodoArchiveItem(key, todoItem) {
  return Database.pushFirebaseEntry(TODOS_ARCHIVE_PATH + "/" + key, todoItem);
}

// Returns the todoItemId of the item that was added alongwith specified key.
// Call for each item you want to add.
export function addNewTodoItemWithNoFBID(key, todoItem) {
  return Database.setFirebaseEntry(key, todoItem);
}

export function addNewItemWithNoFBID(key, value) {
  return Database.setFirebaseEntry(key, value);
}

// Call for each item you want to remove.
export function removeTodoItem(todoItemId) {
  Database.doOnReadingFirebaseEntry(`${TODOS_PATH}/${todoItemId}`, snapshot => {
    if (snapshot.exists()) {
      let chatPath = snapshot.val().chatPath;
      if (chatPath) {
        Database.setFirebaseEntry(`${chatPath}/todoPath`, null);
      }
    }
    Database.setFirebaseEntry(`${TODOS_PATH}/${todoItemId}`, null);
  });
}

export function removeItem(path) {
  Database.doOnReadingFirebaseEntry(path, snapshot => {
    if (snapshot.exists()) {
      let chatPath = snapshot.val().chatPath;
      if (chatPath) {
        Database.setFirebaseEntry(`${chatPath}/todoPath`, null);
      }
    }
    Database.setFirebaseEntry(path, null);
  });
}

export async function getTodoItemAsync(todoItemId) {
  return await Database.getFirebaseEntryAsync(`${TODOS_PATH}/${todoItemId}`);
}

// Returns property value of passed in property name on passed in path
export async function getPropertyAsync(path, propertyName) {
  if (propertyName != undefined) {
    return await Database.getFirebaseEntryAsync(path + '/' + propertyName);
  }
  else {
    return await Database.getFirebaseEntryAsync(path);
  }
}

export function doOnGettingTodo(todoPath, callback) {
  Database.doOnReadingFirebaseEntry(`${TODOS_PATH}/${todoPath}`, snapshot => {
    if (snapshot.exists()) {
      callback(snapshot);
    } else {
      Database.doOnReadingFirebaseEntry(`${TODOS_ARCHIVE_PATH}/${todoPath}`, snapshot => {
        callback(snapshot);
      });
    }
  });
}

export function updateTodoItem(todoPath, description, importance, notes, status, updatedTimestamp, adminId) {
  Database.doOnReadingFirebaseEntry(`${TODOS_PATH}/${todoPath}`, snapshot => {
    if (snapshot.exists()) {
      let updatedTodo = snapshot.val();
      updatedTodo.description = description;
      updatedTodo.importance = importance ? importance : 'normal';
      if (status != undefined) {
        updatedTodo.status = status;
      }

      if (notes != undefined) {
        updatedTodo.notes = notes;
      }

      if (updatedTimestamp) {
        updatedTodo.updatedTimestamp = updatedTimestamp;
      }

      if (adminId) {
        updatedTodo.lastAdminToEdit = adminId;
      }

      Database.setFirebaseEntry(`${TODOS_PATH}/${todoPath}`, updatedTodo);
    } else {
      Database.doOnReadingFirebaseEntry(`${TODOS_ARCHIVE_PATH}/${todoPath}`, snapshot => {
        if (snapshot.exists()) {
          let updatedTodo = snapshot.val();
          updatedTodo.description = description;
          updatedTodo.importance = importance;
          if (status != undefined) {
            updatedTodo.status = status;
          }

          if (notes != undefined) {
            updatedTodo.notes = notes;
          }

          if (updatedTimestamp) {
            updatedTodo.updatedTimestamp = updatedTimestamp;
          }

          if (adminId) {
            updatedTodo.lastAdminToEdit = adminId;
          }

          Database.setFirebaseEntry(`${TODOS_ARCHIVE_PATH}/${todoPath}`, updatedTodo);
        }
      });
    }
  });
}

export function getTimestampString(timestamp) {
  return timestamp ? (new Date(timestamp)).toLocaleString('en-US') : '';
}

export function getAdminLastCheckedNotesTime(todo, adminId) {
  return _.get(todo, `adminStatus.${adminId}.lastCheckedNotes`);
}

export function setAdminLastCheckedNotesTime(todoPath, adminId) {
  Database.doOnReadingFirebaseEntry(`${TODOS_PATH}/${todoPath}`, snapshot => {
    if (snapshot.exists()) {
      Database.setFirebaseEntryAsync(`${TODOS_PATH}/${todoPath}/adminStatus/${adminId}/lastCheckedNotes`, Date.now());
    } else {
      Database.doOnReadingFirebaseEntry(`${TODOS_ARCHIVE_PATH}/${todoPath}`, (snapshot2) => {
        if (snapshot2.exists()) {
          Database.setFirebaseEntryAsync(`${TODOS_ARCHIVE_PATH}/${todoPath}/adminStatus/${adminId}/lastCheckedNotes`, Date.now());
        }
      });
    }
  });
}

export function getTimestampFromTodoList(todoList) {
  // Argument = a todo group in the form of
  // {
  //    <todo id>: {todoId: <string>, logId, <string>, dataEntryContent: {...}, ...}
  //    <todo id>: {...}
  // }
  const todoIds = Object.keys(todoList);
  const now = Date.now();
  let oldestTimestamp = now;

  for (const key of todoIds) {
    const dataEntryContent = todoList[key].dataEntryContent;
    if (!dataEntryContent || dataEntryContent.needsHelp) {
      // Note: !dataEntryContent can be true for older todos from chatBot days.
      continue; // Skip looking at timestamps for todos marked Need Help
    }

    const loggedTimestamps = dataEntryContent.loggedTimestamps;
    if (loggedTimestamps && loggedTimestamps.length > 0) {
      let latestTimestamp = loggedTimestamps[loggedTimestamps.length - 1].timestamp;

      // If revisionHistory only has source: "user" loggedTimstamps but revisionHistory contains a "You logged:"
      // then that means this TODO was copied over from a previous log and then the user made a correction to it.
      // In this case, just take the most recent timestamp as the oldest timestamp.
      // Reason: A user could have copied over a food many hours ago and then decided to correct it. =
      // We don't want to assume the oldest timestamp was many hours ago in this case
      if (!isCopiedThenCorrectedFood(dataEntryContent.loggedTimestamps, dataEntryContent.revisionHistory)) {
        // Otherwise, check for which is the oldest source: "user" loggedTimestamp before the most recent source: <anything other than "user">
        for (let i = loggedTimestamps.length - 1; i >= 0; i--) {
          const currData = loggedTimestamps[i];
          if (currData.source === 'user') {
            latestTimestamp = currData.timestamp;
          } else {
            break;
          }
        }
      }

      if (latestTimestamp < oldestTimestamp) {
        oldestTimestamp = latestTimestamp;
      }
    }
  }

  const timePassed = now - oldestTimestamp;
  return { timestamp: oldestTimestamp, timePassed: timePassed };
}


// WEBPACK FOOTER //
// ./src/helpers/TodoUtil.js